import InfiniteScrollContent from "@/components/InfiniteScrollContent";
import VirtualList from "@/components/virtual-list";
import { getRatio } from "@/utils/pxtorem";
import useNavigation from "@/utils/useNavigate";
import { ActivityCardModel, ActivityModel } from "@akc/biz-api";
import { ActivityListModel, ItemType, useActivityList } from "@akc/biz-logic";
import {
  ActivityCardNormalV2Comp,
  CardSlotComp,
  ResourceHotRankComp,
} from "@akc/mx-taro-h5-components";
import { InfiniteScroll, Skeleton } from "antd-mobile";
import Cookie from "js-cookie";
import React, { useCallback, useState } from "react";
import styles from "./ActivityList.module.less";
import B2RShopList from "./B2RShopList";
import Banner from "./Banner";
import FilterBar from "./FilterBar";

type Props = {
  activityTagIndex?: number; //当前类目tab排序，用于埋点
  activityTagNo: string;
  activityTagName?: string; //当前类目tab名称，用于埋点
  pageId: string;
  previousPageName?: string;
};

const ActivityList: React.FC<Props> = ({
  activityTagIndex = 0,
  activityTagNo,
  activityTagName,
  previousPageName,
}) => {
  const { navigate2H5, navigate2Product } = useNavigation();
  const [filterInfo, setFilterInfo] = useState<any>({
    sortModel: 1,
    sortType: 2,
  });

  // 获取最近10条用户点击的店铺ID，若返回大于10条只取最后10条
  const getShopCodes = useCallback(() => {
    return [];
  }, []);

  const ratio = getRatio();

  // 计算卡片高度字段height的方法， 用于在虚拟列表中计算top便宜
  const measureHeight = useCallback(async (_isLarge: boolean, curActivity?: ActivityListModel) => {
    if (!curActivity) {
      return 0;
    }
    switch (curActivity?.type) {
      // 单个图片资源
      case ItemType.NormalCard: {
        const model = curActivity.data as ActivityCardModel;
        return ((model.imageHeight ?? 0) / ((model.imageWidth ?? 0) / 355)) * ratio;
      }
      // 会场资源位
      case ItemType.ConferenceCard: {
        const model = curActivity.data as ActivityCardModel;
        return ((model.imageHeight ?? 0) / ((model.imageWidth ?? 0) / 355) + 50) * ratio;
      }
      // B2R店铺
      case ItemType.B2rShopCard:
        return 195 * ratio;
      // 榜单插卡
      case ItemType.HotRank:
        return 154 * ratio;
      // 档期列表
      default: {
        const model = curActivity.data as ActivityModel;
        if (
          model?.labelLocationVOList?.find(
            item =>
              item.locationCode === "H5_ScheduleList_BrandDown" &&
              (item.labelInfoVOList?.length ?? 0) > 0,
          ) ||
          model?.isShowEndTime
        ) {
          return 230 * ratio;
        } else {
          return 206 * ratio;
        }
      }
    }
  }, []);

  const {
    data: activityList,
    hasNext,
    onLoadMore,
    isLoading,
    error,
  } = useActivityList({
    pageId: "H5_ScheduleList",
    categoryId: activityTagNo,
    activityStatus: 2,
    sortModel: filterInfo.sortModel,
    sortType: filterInfo.sortType,
    isLarge: false,
    scene: "INDEX",
    getShopCodes,
    measureHeight,
    userId: Cookie.get("msUserCode") || "userId",
  });

  const onDetailAction = actInfo => {
    console.log(actInfo);
    navigate2H5(actInfo.ringshopLinkUrl);
  };
  const onForwardAction = actInfo => {
    console.log(actInfo);
    navigate2H5(actInfo.ringshopLinkUrl);
  };

  return (
    <>
      <div className={styles.container}>
        {/* 筛选栏 */}
        <FilterBar filterInfo={filterInfo} setFilterInfo={setFilterInfo} />
        {/* 档期feed流 */}
        <div className={styles.waterfallWrapper}>
          <div className={styles.waterfallContainer}>
            {(activityList?.length ?? 0) === 0 && hasNext ? (
              new Array(3)
                .fill(0)
                .map((_, index) => (
                  <Skeleton key={index} animated className={styles.cardSkeleton} />
                ))
            ) : (
              <>
                <div className={styles.listWrap}>
                  <VirtualList
                    dataList={activityList ?? []}
                    childRender={(item, index) => {
                      switch (item.type) {
                        // 单个图片资源（新版可能没这个类型了）
                        case ItemType.NormalCard: {
                          return <Banner dataList={[item.data]} />;
                        }
                        // 会场资源位（新版可能没这个类型了）
                        case ItemType.ConferenceCard: {
                          return (
                            <CardSlotComp
                              activityCard={item.data}
                              onDetailAction={onDetailAction}
                              onForwardAction={onForwardAction}
                            />
                          );
                        }
                        // 店铺卡片资源位
                        case ItemType.B2rShopCard:
                          return (
                            <B2RShopList
                              dataList={(item.data as ActivityCardModel).shopVOList ?? []}
                              trackData={{
                                domain: "首页",
                              }}
                            />
                          );
                        // 横向榜单卡片资源位
                        case ItemType.HotRank:
                          return (
                            <ResourceHotRankComp
                              data={item.data.cardHotRankVO}
                              style={{ margin: 0 }}
                              index={index}
                              trackData={{
                                page_name: "首页",
                                previous_page_name: previousPageName,
                                tab_name: activityTagName, // 当前组件所在tab/分类的名称，枚举值：全部、女装等
                                tab_rank: activityTagIndex + 1, // 当前所在tab/分类的排序，从左至右，1开始
                                resource_name: item?.title, // 榜单的名称，例如“连衣裙热销榜”
                                resource_type: "榜单",
                                comp_name: "档期feed流",
                                comp_type: "feed流",
                                resource_rank: index,
                                comp_index: 1, //	组件在横向导航内的相对排序，从1开始,这个取不到
                                relative_rank: index + 1, //组件的在整个页中的排序，从1开始；
                              }}
                            />
                          );
                        // 档期卡片
                        default: {
                          return (
                            <ActivityCardNormalV2Comp
                              model={item.data}
                              trackData={{
                                domain: "首页",
                              }}
                              mainLocationCode="H5_ScheduleList_BrandDown"
                              productTLLocationCode="H5_ScheduleList_TopProductPicLeft"
                              productTRLocationCode="H5_ScheduleList_TopProductPicRight"
                            />
                          );
                        }
                      }
                    }}
                    itemHeight={index => {
                      const curActivity = activityList?.[index];
                      return curActivity?.height ?? 0;
                    }}
                    marginY={8 * ratio}
                  />
                </div>
                <InfiniteScroll loadMore={onLoadMore} hasMore={hasNext} threshold={250}>
                  <InfiniteScrollContent hasMore={hasNext} />
                </InfiniteScroll>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivityList;
